import { Head } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { AnalyticsErrorState } from "@/components/brand/analytics/analytics-error-state"
import { BrandAnalyticsTabs } from "@/components/brand/analytics/brand-analytics-tabs"
import { EmvEmptyState } from "@/components/brand/analytics/emv/emv-empty-state"
import { EmvKpiStrip } from "@/components/brand/analytics/emv/emv-kpi-strip"
import { EmvMissionsTable } from "@/components/brand/analytics/emv/emv-missions-table"
import { EmvPostsTable } from "@/components/brand/analytics/emv/emv-posts-table"
import { EmvProgressionChart } from "@/components/brand/analytics/emv/emv-progression-chart"
import { EmvRangeToggle } from "@/components/brand/analytics/emv/emv-range-toggle"
import AppLayout from "@/layouts/app-layout"
import type { EmvAnalyticsProps } from "@/types/emv-analytics"

/**
 * OKS-503 — Brand EMV dashboard (`/brand/analytics/emv`).
 *
 * Third tab on the brand analytics surface (Missions · Crew · EMV).
 * Per-post + per-mission Earned Media Value with day-over-day
 * progression over a selectable range.
 *
 * # State branch (TRD §4 / KD-5)
 *
 *   1. `state === "error"`      → `<AnalyticsErrorState>` (Retry).
 *   2. `state === "cold_start"` → `<EmvEmptyState>` (no tracked posts).
 *   3. `state === "empty_in_window"` / `"success"` → range toggle + KPI
 *      strip + progression chart + mission roll-up + paginated post
 *      table. The empty-in-window zero/empty variants are handled INSIDE
 *      each component (KPIs at $0, chart + tables render their empty
 *      cards) — no separate page branch, so layout stays stable.
 *
 * Loading is handled by Inertia's global progress indicator + content
 * preservation on the range/pagination partial reloads (no spinner;
 * the previous data stays mounted until the swap settles).
 *
 * # Partial reload
 *
 * Range toggle + pagination route via `router.get` with
 * `only: ['state','range','kpis','progression','missions','posts_page']`.
 * The shell + tab strip + header stay mounted.
 */
export default function BrandAnalyticsEmvIndex({
  state,
  range,
  kpis,
  progression,
  missions,
  posts_page: postsPage,
}: EmvAnalyticsProps) {
  const { t } = useTranslation()

  const reloadKeys = [
    "state",
    "range",
    "kpis",
    "progression",
    "missions",
    "posts_page",
  ]

  return (
    <AppLayout>
      <Head title={t("emv.head_title")} />
      <div className="flex flex-col gap-6 p-4 md:p-6" data-testid="emv-page">
        <BrandAnalyticsTabs active="emv" />

        <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold tracking-tight">
              {t("emv.heading")}
            </h1>
            <p className="text-muted-foreground text-sm">
              {t("emv.subheading")}
            </p>
          </div>
          {/* Range toggle stays visible in every non-error state (incl.
           * cold_start), so the brand can switch windows even when empty. */}
          {state !== "error" ? <EmvRangeToggle value={range} /> : null}
        </header>

        {state === "error" ? (
          <AnalyticsErrorState reloadOnly={reloadKeys} />
        ) : state === "cold_start" ? (
          <EmvEmptyState />
        ) : (
          <>
            {kpis ? <EmvKpiStrip kpis={kpis} /> : null}
            {progression ? (
              <EmvProgressionChart
                progression={progression}
                totalCents={kpis?.total_emv_cents ?? 0}
              />
            ) : null}
            {missions ? <EmvMissionsTable missions={missions} /> : null}
            {postsPage ? (
              <EmvPostsTable postsPage={postsPage} range={range} />
            ) : null}
          </>
        )}
      </div>
    </AppLayout>
  )
}
